import { observer } from "mobx-react-lite"

import type { CreatePositionStore } from "@/domains/thick-v2/stores/create-position-store"
import type { PoolOhlcStore } from "@/domains/thick-v2/stores/pool-ohlc-store"
import type { ThickV2TickDistributionStore } from "@/domains/thick-v2/tick-distribution"
import { CreatePositionRangeBrushComposite } from "@/domains/thick-v2/components/range-brush/create-position-range-brush-composite"

import { CreatePositionCardView } from "./create-position-card-view"
import { CreatePositionProcessView } from "./create-position-process-view"
import { PriceHistoryComposite } from "./price-history-composite"

export interface CreatePositionCardCompositeProps {
  store: CreatePositionStore
  /**
   * Thick-v1 tick distribution store driving the liquidity-profile range
   * brush. Optional so callers that do not yet have access to the shared
   * instance (pre-t32 ownership move) still render cleanly — the brush slot
   * is simply omitted when absent.
   */
  tickDistributionStore?: ThickV2TickDistributionStore
  /**
   * Shared pool OHLC store. Optional so unit tests / Storybook can render the
   * card without wiring the historical-price composite; when absent, the
   * view simply omits the price-history slot.
   */
  poolOhlcStore?: PoolOhlcStore
}

/**
 * Composite for the create-position card. Reads the `CreatePositionStore`
 * observables and translates them into plain DTO props + callbacks for the
 * dumb {@link CreatePositionCardView}.
 *
 * All MobX coupling lives here; the view is safe to render in Storybook with
 * static fixtures. The range-brush composite is mounted here and handed to
 * the view as an opaque `rangeBrush` slot so the view doesn't own another
 * composite's lifecycle.
 */
export const CreatePositionCardComposite = observer(
  ({ store, tickDistributionStore, poolOhlcStore }: CreatePositionCardCompositeProps) => {
    const pool = store.pool
    if (!pool) return null

    const currentPrice = store.displayedCurrentPrice
    const priceRangeCalculation = store.priceRangeCalculation
    const priceBaseSymbol = store.isPriceRangeInverted ? pool.token1.symbol : pool.token0.symbol
    const priceQuoteSymbol = store.isPriceRangeInverted ? pool.token0.symbol : pool.token1.symbol

    const formattedPrice = currentPrice
      ? `${currentPrice.toLocaleString(undefined, {
          minimumFractionDigits: 5,
          maximumFractionDigits: 5,
        })} ${priceQuoteSymbol}/${priceBaseSymbol}`
      : "Price unavailable"

    const token0UsdPrice = pool.token0.bestPrice
      ? `$${pool.token0.bestPrice.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 6,
        })}`
      : undefined

    const minPrice = store.displayedPricesAtTicks[0]
    const maxPrice = store.displayedPricesAtTicks[1]
    const minPriceDifference = store.displayedPriceDifferences.min
    const maxPriceDifference = store.displayedPriceDifferences.max
    const minTickAtLimit = store.isPriceRangeInverted
      ? priceRangeCalculation.ticksAtLimit[1]
      : priceRangeCalculation.ticksAtLimit[0]
    const maxTickAtLimit = store.isPriceRangeInverted
      ? priceRangeCalculation.ticksAtLimit[0]
      : priceRangeCalculation.ticksAtLimit[1]
    const onIncrementMinPrice = store.isPriceRangeInverted
      ? () => store.incrementMaxPrice()
      : () => store.incrementMinPrice()
    const onDecrementMinPrice = store.isPriceRangeInverted
      ? () => store.decrementMaxPrice()
      : () => store.decrementMinPrice()
    const onIncrementMaxPrice = store.isPriceRangeInverted
      ? () => store.incrementMinPrice()
      : () => store.incrementMaxPrice()
    const onDecrementMaxPrice = store.isPriceRangeInverted
      ? () => store.decrementMinPrice()
      : () => store.decrementMaxPrice()

    const isPriceStrategyDisabled =
      store.tickSpacing === undefined || store.currentTick === undefined

    const token0BalanceLabel = `Balance: ${pool.token0.displayUnits(pool.token0.balance, 4)} ${pool.token0.symbol}`
    const token1BalanceLabel = `Balance: ${pool.token1.displayUnits(pool.token1.balance, 4)} ${pool.token1.symbol}`

    const isWalletConnected = store.isWalletConnected
    const canCreatePosition = store.canCreatePosition
    const ctaLabel = resolveCtaLabel(store)

    // Show the in-card stepper whenever the interaction is mid-flight,
    // has just finished (so the user sees the final checkmark before the
    // card reverts), or is sitting on a failed step waiting for a retry.
    // The store drives all three cases via `activeStep` / `completedSteps`
    // / `stepErrorMessage`, so the composite only needs the boolean.
    const isProcessVisible =
      store.isCreating ||
      store.activeStep !== undefined ||
      store.completedSteps.length > 0

    const processView = isProcessVisible ? (
      <CreatePositionProcessView
        steps={store.processSteps}
        errorMessage={store.stepErrorMessage}
        onCancel={() => store.clearStepProgress()}
        onCheckStatus={() => void store.syncProcessFromChain()}
        isCheckingStatus={store.isCheckingProcess}
      />
    ) : undefined

    const rangeBrush = tickDistributionStore ? (
      <CreatePositionRangeBrushComposite
        createPositionStore={store}
        tickDistributionStore={tickDistributionStore}
        poolOhlcStore={poolOhlcStore}
      />
    ) : undefined

    const priceHistory = poolOhlcStore ? (
      <PriceHistoryComposite createPositionStore={store} poolOhlcStore={poolOhlcStore} />
    ) : undefined

    return (
      <CreatePositionCardView
        token0={pool.token0}
        token1={pool.token1}
        displayFee={pool.tickSpacing === undefined ? pool.displayFee : `Spacing ${pool.tickSpacing}`}
        formattedPrice={formattedPrice}
        token0UsdPrice={token0UsdPrice}
        tvlUsd={pool.tvlUsd}
        volumeUsd24h={pool.volumeUsd24h}
        feesUsd24h={pool.feesUsd24h}
        estimatedApr={store.estimatedApr}
        isProposedRangeInRange={store.isProposedRangeInRange}
        liquidityCoveragePct={store.liquidityCoveragePct}
        priceStrategyConfigs={store.priceStrategyConfigs}
        selectedStrategy={store.selectedStrategy}
        isPriceStrategyDisabled={isPriceStrategyDisabled}
        onSelectStrategy={(strategy) => store.applyPriceStrategy(strategy)}
        rangeSpreadPctInput={store.rangeSpreadPctInput}
        isRangeSpreadDisabled={isPriceStrategyDisabled}
        onSetRangeSpreadPctInput={(value) => store.setRangeSpreadPctInput(value)}
        priceHistory={priceHistory}
        rangeBrush={rangeBrush}
        minPrice={minPrice}
        maxPrice={maxPrice}
        minPriceDifference={minPriceDifference}
        maxPriceDifference={maxPriceDifference}
        minTickAtLimit={minTickAtLimit}
        maxTickAtLimit={maxTickAtLimit}
        onIncrementMinPrice={onIncrementMinPrice}
        onDecrementMinPrice={onDecrementMinPrice}
        onIncrementMaxPrice={onIncrementMaxPrice}
        onDecrementMaxPrice={onDecrementMaxPrice}
        amount0={store.amount0}
        amount1={store.amount1}
        token0BalanceLabel={token0BalanceLabel}
        token1BalanceLabel={token1BalanceLabel}
        onFillMaxAmount0={() => store.fillMaxAmount0()}
        onFillMaxAmount1={() => store.fillMaxAmount1()}
        onSetAmount0={(value) => store.setAmount0FromUserInput(value)}
        onSetAmount1={(value) => store.setAmount1FromUserInput(value)}
        onFocusToken0={() => store.setActiveDepositInput("token0")}
        onFocusToken1={() => store.setActiveDepositInput("token1")}
        totalDepositUsdValue={store.totalDepositUsdValue}
        isWalletConnected={isWalletConnected}
        isToken0DepositDisabled={store.isToken0DepositDisabled}
        isToken1DepositDisabled={store.isToken1DepositDisabled}
        canCreatePosition={canCreatePosition}
        ctaLabel={ctaLabel}
        onCreatePosition={() => store.startNextStepForConnectedWallet()}
        processView={processView}
        isRangeInvalid={store.isRangeInvalid}
      />
    )
  },
)

/**
 * Map the store's `ctaState` into a label the view can render verbatim.
 * Kept here (not in the store) so we never import display strings into
 * the domain layer and the store stays locale-agnostic.
 */
function resolveCtaLabel(store: CreatePositionStore): string {
  const pool = store.pool
  switch (store.ctaState) {
    case "connect":
      return "Connect wallet"
    case "enter-amount":
      return "Enter an amount"
    case "loading-approvals":
      return "Checking approvals…"
    case "approve-token0":
      return pool ? `Approve ${pool.token0.symbol}` : "Approve token"
    case "approve-token1":
      return pool ? `Approve ${pool.token1.symbol}` : "Approve token"
    case "create-position":
      return "Create position"
    case "in-flight":
      return "Creating position…"
    default:
      return "Create position"
  }
}
